import { Box, Skeleton, Stack, Typography } from '@mui/material';
import {
  SchoolYear,
  Timetable,
  useGetTimetablesQuery,
  useGetTimetableStructuresQuery,
} from '@schooly/api';
import { useAuth } from '@schooly/components/authentication';
import { PageHeader } from '@schooly/components/filters';
import { useFacilities } from '@schooly/hooks/use-facilities';
import { TIMETABLE } from '@schooly/constants/test-ids';
import { AttendanceStubSvg, PlusIcon, SelectPopover, SimpleButton } from '@schooly/style';
import { FC, useCallback, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link, Outlet } from 'react-router-dom';

import MainLayout from '../../../components/uikit-components/MainLayout/MainLayout';
import { useSchool } from '../../../hooks/useSchool';
import useSchoolYears from '../../../hooks/useSchoolYears';
import { useTimetablePermissions } from '../../../hooks/useTimetablePermissions';
import { SchoolFacilitiesEmptyCard } from './SchoolFacilitiesEmptyCard';
import { TimetableSetupSteps } from './TimetableSetupSteps';
import { mapStructureFromResponse, TimetableStructureSection } from './TimetableStructureSection';

export const SchoolTimetables: FC = () => {
  const { schoolId = '' } = useSchool();
  const { formatMessage } = useIntl();
  const { permissions } = useAuth();
  const isAdmin = permissions?.includes('school_admin');
  const { canManageTimetables } = useTimetablePermissions();

  const {
    schoolYears,
    defaultValidity,
    nextSchoolYear,
    getSchoolYearById,
    isPreviousSchoolYear,
    isLoading: isLoadingSchoolYears,
  } = useSchoolYears();

  const displaySchoolYears = useMemo(() => {
    return [
      ...(defaultValidity?.isActual ? [defaultValidity] : []),
      ...(nextSchoolYear ? [nextSchoolYear] : []),
    ];
  }, [defaultValidity, nextSchoolYear]);

  const {
    data: timetablesData,
    isLoading: isLoadingTimetables,
    params,
    setParams,
  } = useGetTimetablesQuery(
    { school_id: schoolId, school_year_id: defaultValidity?.id ?? '' },
    {
      refetchOnMount: 'always',
      enabled: !!schoolId && !!defaultValidity?.id,
    },
  );

  const hasTimetablesInYear = !!timetablesData?.timetables?.length && !isLoadingTimetables;

  const { data: allTimetablesData, isLoading: isLoadingAllTimetables } = useGetTimetablesQuery(
    { school_id: schoolId },
    { refetchOnMount: 'always', enabled: !!schoolId && !hasTimetablesInYear },
  );

  const hasAnyTimetables = hasTimetablesInYear || !!allTimetablesData?.timetables?.length;

  const {
    facilities,
    isLoading: isLoadingFacilities,
    rooms,
  } = useFacilities({ schoolId }, { enabled: !!schoolId });
  const hasEmptyFacilities = !isLoadingFacilities && !facilities.length;

  const selectedYear = useMemo(
    () => (params.school_year_id ? getSchoolYearById(params.school_year_id) : undefined),
    [params.school_year_id, getSchoolYearById],
  );

  const isPastYear = selectedYear ? isPreviousSchoolYear(selectedYear) : false;

  const { data: structuresData, isLoading: isLoadingStructures } = useGetTimetableStructuresQuery(
    { school_id: schoolId },
    { enabled: !!schoolId, refetchOnMount: 'always' },
  );

  const structures = isPastYear
    ? (timetablesData?.structures ?? [])
    : (structuresData?.results ?? []);

  const hasStructures = !!structures?.length;

  const handleSchoolYearSelect = useCallback(
    (option: SchoolYear) => {
      setParams((prev) => ({ ...prev, school_year_id: option.id }));
    },
    [setParams],
  );

  const timetablesByStructure = useMemo(() => {
    const map: Record<string, Timetable[]> = {};

    for (const timetable of timetablesData?.timetables ?? []) {
      if (!map[timetable.structure_id]) {
        map[timetable.structure_id] = [];
      }
      map[timetable.structure_id].push(timetable);
    }

    return map;
  }, [timetablesData]);

  const isLoading =
    isLoadingSchoolYears ||
    isLoadingStructures ||
    isLoadingTimetables ||
    (!hasTimetablesInYear && isLoadingAllTimetables);

  const renderContent = () => {
    if (isLoading) {
      return (
        <Stack gap={2} pt={2}>
          <Skeleton variant="rectangular" height={150} />
          <Skeleton variant="rectangular" height={150} />
        </Stack>
      );
    }

    if (!hasAnyTimetables) {
      return (
        <TimetableSetupSteps
          schoolYears={displaySchoolYears}
          structures={structures}
          rooms={rooms}
          isAdmin={isAdmin}
        />
      );
    }

    if (!hasStructures && isPastYear) {
      const noTimetablesLabel = formatMessage({ id: 'timetable-NoTimetables' });
      return <NoTimetablesStub label={noTimetablesLabel} />;
    }

    return (
      <Box pt={2.5}>
        {hasEmptyFacilities && <SchoolFacilitiesEmptyCard isAdmin={isAdmin} />}

        {structures?.map((structure) => {
          const structureInfo = mapStructureFromResponse(structure);
          const timetables = timetablesByStructure[structure.id] || [];

          return (
            <TimetableStructureSection
              key={structure.id}
              structure={structureInfo}
              timetables={timetables}
              schoolId={schoolId}
              schoolYearId={selectedYear?.id || ''}
              isPastYear={isPastYear}
              canManageTimetables={canManageTimetables}
            />
          );
        })}
      </Box>
    );
  };

  return (
    <MainLayout>
      <Stack sx={{ height: 'calc(100vh - 32px)', overflow: 'hidden' }}>
        {(isLoading || hasAnyTimetables) && (
          <PageHeader
            pageTitle={
              !!schoolYears.length ? (
                <Stack direction="row" gap={1}>
                  <FormattedMessage id="school-sections-Timetable" />
                  <SelectPopover
                    options={schoolYears}
                    selectedOption={selectedYear}
                    onSelect={handleSchoolYearSelect}
                    dataTestId={TIMETABLE.SCHOOL_YEAR_SELECT}
                  >
                    <Link to="/settings/school_years">
                      <SimpleButton startIcon={<PlusIcon />} sx={{ mt: 1 }}>
                        <FormattedMessage id="school-tabs-SchoolYears-AddSchoolYear" />
                      </SimpleButton>
                    </Link>
                  </SelectPopover>
                </Stack>
              ) : (
                <FormattedMessage id="school-sections-Timetable" />
              )
            }
          />
        )}

        <Box sx={{ flex: 1, overflowY: 'auto' }}>{renderContent()}</Box>
      </Stack>

      <Outlet />
    </MainLayout>
  );
};

export const NoTimetablesStub: FC<{ label: string }> = ({ label }) => {
  return (
    <Stack alignItems="center" justifyContent="center" height="100%" gap={2.5}>
      <img src={AttendanceStubSvg} alt={label} />
      <Typography variant="h3" color="common.grey2">
        {label}
      </Typography>
    </Stack>
  );
};
